import { useRef } from "react"
import { Button, Center, Drawer, Loader, Text, Group, CloseButton } from "@mantine/core"
import { Trans, useTranslation } from "react-i18next"
import { useUser } from "../../user/hooks/useUser"
import { modals } from "@mantine/modals"
import { useTravelersCheck, useDeleteTravelersCheck } from "../hooks/useTravelersChecks"
import TravelersCheckDetailContent from "./TravelersCheckDetailContent"
import TravelersCheckFormContent from "./TravelersCheckFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedCheckId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function TravelersCheckDrawer({ opened, onClose, mode, selectedCheckId, onDeleted, onSwitchToEdit }: Props) {
  const { t } = useTranslation()
  const { data: check, isLoading } = useTravelersCheck(mode !== "create" ? selectedCheckId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageTravelerChecks ?? false
  const deleteMutation = useDeleteTravelersCheck()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("travelersChecks.confirmDelete"),
      children: <Text size="sm">{t("travelersChecks.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(check!.id, {
          onSuccess: () => {
            onDeleted()
          },
        })
      },
    })
  }

  const title =
    mode === "create"
      ? t("travelersChecks.createTitle")
      : mode === "edit"
        ? t("travelersChecks.editTitle")
        : `${t("travelersChecks.title")} #${selectedCheckId}`

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md">{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <TravelersCheckFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !check ? (
        <Text>
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <TravelersCheckFormContent ref={formRef} check={check} onSuccess={onClose} />
      ) : (
        <TravelersCheckDetailContent
          check={check}
        />
      )}
    </Drawer>
  )
}
